import { useCallback, useEffect, useRef, useState } from "react";
import { Desktop, DeviceMobile, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import WaTemplates from "../WaTemplates";

export const CAMPAIGN_VARS = [
  { v: "{{nome_cliente}}", label: "Nome" },
  { v: "{{nome_completo}}", label: "Nome completo" },
  { v: "{{data_prenotazione}}", label: "Data pren." },
  { v: "{{ora}}", label: "Ora" },
  { v: "{{ristorante}}", label: "Ristorante" },
  { v: "{{nome_coupon}}", label: "Nome coupon" },
  { v: "{{codice_coupon}}", label: "Codice coupon" },
  { v: "{{scadenza_coupon}}", label: "Scadenza coupon" },
];

const PREVIEW_CTX = {
  "{{nome_cliente}}": "Giulia", "{{nome_completo}}": "Giulia Bianchi", "{{data_prenotazione}}": "25/08",
  "{{ora}}": "20:00", "{{ristorante}}": "Ristorante Da Lucia", "{{nome_coupon}}": "Sconto 20% Cena",
  "{{codice_coupon}}": "DH-AB12CD", "{{scadenza_coupon}}": "30/09",
};

export function renderPreview(text) {
  return Object.entries(PREVIEW_CTX).reduce((acc, [k, v]) => acc.replaceAll(k, v), text || "");
}

const WA_STATUS = {
  approvato: "bg-emerald-100 text-emerald-800 border-emerald-300",
  in_revisione: "bg-amber-100 text-amber-800 border-amber-300",
  rifiutato: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function ChannelContentEditor({ channel, content, onChange, campaignId, recipients }) {
  const [previewMode, setPreviewMode] = useState("mobile");
  const [waTemplates, setWaTemplates] = useState([]);
  const [showTplManager, setShowTplManager] = useState(false);
  const [testDest, setTestDest] = useState("");
  const [testResult, setTestResult] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/marketing/uploads/image", fd);
      set("attachment_url", r.data.url);
      toast.success("Immagine caricata — la vedi in anteprima qui sotto");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const loadTpl = useCallback(() => {
    if (channel === "whatsapp_api") {
      api.get("/integrations/whatsapp/templates/approved").then((r) => setWaTemplates(r.data)).catch(() => {});
    }
  }, [channel]);

  useEffect(() => { loadTpl(); }, [loadTpl]);

  const set = (k, v) => onChange((prev) => ({ ...(typeof prev === "object" && prev ? prev : content), [k]: v }));
  const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
  const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";
  const body = content.body || "";
  const smsCount = Math.max(1, Math.ceil(body.length / 160));

  const waTemplate = waTemplates.find((x) => x.name === content.wa_meta_template);
  let waPreview = "";
  if (channel === "whatsapp_api" && waTemplate) {
    waPreview = (waTemplate.components || []).find((c) => c.type === "BODY")?.text || "";
    (content.wa_params || []).forEach((v, i) => {
      waPreview = waPreview.replaceAll(`{{${i + 1}}}`, renderPreview(v) || `{{${i + 1}}}`);
    });
  }

  const insertVar = (v) => set("body", body + v);

  const sendTest = async () => {
    if (!campaignId) { toast.error("Salva prima la bozza (vai allo step successivo e torna indietro)"); return; }
    if (!testDest.trim()) { toast.error("Inserisci un numero o una email di test"); return; }
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/test`, { destination: testDest.trim(), content });
      setTestResult(r.data);
      if (r.data.sent && !r.data.mock) {
        toast.success(`Inviato davvero a ${r.data.destination} — controlla la casella!`);
      } else if (r.data.detail) {
        toast.warning(r.data.detail);
      } else {
        toast.success("Anteprima di test generata");
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid={`content-${channel}`}>
      {channel === "email" && (
        <>
          <div><label className={labelCls}>Oggetto</label>
            <input value={content.subject || ""} onChange={(e) => set("subject", e.target.value)} className={inputCls} data-testid="email-subject" /></div>
          <div><label className={labelCls}>Template grafico</label>
            <select value={content.template || "base"} onChange={(e) => set("template", e.target.value)} className={inputCls} data-testid="email-template">
              <option value="base">Base</option>
              <option value="promozionale">Promozionale</option>
              <option value="elegante">Elegante</option>
            </select></div>
        </>
      )}

      {channel === "whatsapp_api" && (
        <>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls}>Template WhatsApp (Meta — solo approvati)</label>
              <button type="button" onClick={() => setShowTplManager(true)} data-testid="wa-manage-templates"
                className="text-[11px] font-bold text-sky-700 hover:underline">Gestisci template</button>
            </div>
            <div className="space-y-2" data-testid="wa-templates">
              {waTemplates.length === 0 && (
                <button type="button" onClick={() => setShowTplManager(true)} data-testid="wa-templates-empty"
                  className="w-full border border-dashed border-zinc-300 rounded-lg p-3 text-xs text-zinc-500 hover:border-sky-400 hover:text-sky-700 transition">
                  Nessun template approvato. Aprilo da <b>Gestisci template</b>, crealo e invialo in revisione a Meta.
                </button>
              )}
              {waTemplates.map((t) => {
                const bodyText = (t.components || []).find((c) => c.type === "BODY")?.text || "";
                const selected = content.wa_meta_template === t.name;
                return (
                  <button type="button" key={t.id} data-testid={`wa-template-${t.name}`}
                    onClick={() => onChange({ ...content, wa_meta_template: selected ? "" : t.name, wa_params: [] })}
                    className={`w-full text-left border rounded-lg p-3 transition ${
                      selected ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-zinc-900 font-mono">{t.name}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300">APPROVATO</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{bodyText}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">Obbligatorio per campagne WhatsApp API: Meta accetta solo template approvati fuori dalla finestra 24h.</p>
          </div>
          {content.wa_meta_template && (() => {
            const t = waTemplates.find((x) => x.name === content.wa_meta_template);
            const bodyText = (t?.components || []).find((c) => c.type === "BODY")?.text || "";
            const nVars = (bodyText.match(/\{\{\d+\}\}/g) || []).length;
            return nVars > 0 ? (
              <div>
                <label className={labelCls}>Parametri dinamici (accetta variabili campagna, es. {"{{nome_cliente}}"})</label>
                <div className="grid grid-cols-2 gap-2">
                  {Array.from({ length: nVars }).map((_, i) => (
                    <input key={i} value={(content.wa_params || [])[i] || ""} placeholder={`{{${i + 1}}} = …`}
                      onChange={(e) => {
                        const p = [...(content.wa_params || [])];
                        p[i] = e.target.value;
                        set("wa_params", p);
                      }}
                      className={inputCls} data-testid={`wa-param-${i}`} />
                  ))}
                </div>
              </div>
            ) : null;
          })()}
          {showTplManager && (
            <div className="fixed inset-0 z-[60] bg-black/40 overflow-y-auto p-4"
              onClick={() => { setShowTplManager(false); loadTpl(); }}>
              <div className="max-w-2xl mx-auto my-8 bg-zinc-50 rounded-xl p-5" onClick={(e) => e.stopPropagation()}
                data-testid="wa-tpl-manager-modal">
                <WaTemplates />
              </div>
            </div>
          )}
        </>
      )}

      {channel !== "whatsapp_api" && (
        <div>
          <label className={labelCls}>{channel === "sms" ? "Testo SMS" : "Contenuto"}</label>
          <textarea rows={channel === "sms" ? 3 : 6} value={body} onChange={(e) => set("body", e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            data-testid="content-body" />
          {channel === "sms" && (
            <div className="text-[11px] text-zinc-500 mt-1" data-testid="sms-counter">
              {body.length} caratteri · {smsCount} SMS per destinatario
            </div>
          )}
        </div>
      )}

      {channel !== "whatsapp_api" && channel !== "sms" && (
        <>
          <div>
            <label className={labelCls}>Variabili dinamiche</label>
            <div className="flex flex-wrap gap-1">
              {CAMPAIGN_VARS.map(({ v, label }) => (
                <button type="button" key={v} onClick={() => insertVar(v)} data-testid={`cvar-${label.replace(/\s/g, "-")}`}
                  className="h-7 px-2 rounded-full bg-zinc-100 border border-zinc-200 text-[11px] font-medium text-zinc-700 hover:bg-zinc-200 transition">
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={labelCls}>Testo CTA</label>
              <input value={content.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} className={inputCls} data-testid="cta-text" /></div>
            <div><label className={labelCls}>Link CTA</label>
              <input value={content.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} className={inputCls} data-testid="cta-url" /></div>
          </div>
          <div>
            <label className={labelCls}>Immagine / allegato</label>
            <div className="flex gap-2">
              <input value={content.attachment_url || ""} onChange={(e) => set("attachment_url", e.target.value)}
                placeholder="URL immagine oppure carica un file →" className={inputCls} data-testid="attachment-url" />
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                data-testid="attachment-file" onChange={uploadImage} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="attachment-upload-btn"
                className="h-10 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold shrink-0 disabled:opacity-40">
                {uploading ? "Carico..." : "Carica file"}
              </button>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">JPEG, PNG o WebP, max 5 MB — il file caricato compare in anteprima e nell'email inviata.</p>
          </div>
        </>
      )}

      {channel === "sms" && (
        <div>
          <label className={labelCls}>Variabili dinamiche</label>
          <div className="flex flex-wrap gap-1">
            {CAMPAIGN_VARS.map(({ v, label }) => (
              <button type="button" key={v} onClick={() => insertVar(v)} data-testid={`cvar-${label.replace(/\s/g, "-")}`}
                className="h-7 px-2 rounded-full bg-zinc-100 border border-zinc-200 text-[11px] font-medium text-zinc-700 hover:bg-zinc-200 transition">
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Anteprima */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className={labelCls}>Anteprima</label>
          {channel === "email" && (
            <div className="flex gap-1">
              {[["mobile", DeviceMobile], ["desktop", Desktop]].map(([m, Icon]) => (
                <button type="button" key={m} onClick={() => setPreviewMode(m)} data-testid={`preview-${m}`}
                  className={`p-1.5 rounded-md border ${previewMode === m ? "bg-zinc-900 text-white border-zinc-900" : "text-zinc-500 border-zinc-200"}`}>
                  <Icon size={14} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`rounded-lg border border-zinc-200 p-4 ${
          channel === "whatsapp" || channel === "whatsapp_api" ? "bg-[#E7F6E9]" : "bg-zinc-50"} ${previewMode === "mobile" ? "max-w-xs" : ""}`}
          data-testid="content-preview">
          {channel === "email" && content.subject && <div className="text-xs font-bold text-zinc-900 mb-1">{renderPreview(content.subject)}</div>}
          <div className="text-sm text-zinc-800 whitespace-pre-wrap">
            {channel === "whatsapp_api"
              ? (waPreview || <span className="text-zinc-400">Seleziona un template approvato...</span>)
              : (renderPreview(body) || <span className="text-zinc-400">Scrivi il contenuto...</span>)}
          </div>
          {content.attachment_url && (
            <img src={content.attachment_url} alt="" className="mt-2 rounded-md max-h-28 object-cover" onError={(e) => (e.target.style.display = "none")} />
          )}
          {content.cta_text && content.cta_url && (
            <button type="button" className="mt-2 h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold">{content.cta_text}</button>
          )}
          {content.cta_text && !content.cta_url && (
            <div className="mt-2 text-[11px] text-amber-600">Aggiungi il link del pulsante: senza URL il bottone non compare nell'email.</div>
          )}
        </div>
      </div>

      {/* Test di invio */}
      <div className="border border-dashed border-zinc-300 rounded-lg p-3 space-y-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Invio di test {channel === "email" ? "(email reale)" : channel === "sms" ? "(SMS reale dal wallet)" : channel === "whatsapp_api" ? "(WhatsApp reale via template Meta)" : "(solo anteprima)"}
        </div>
        <div className="flex gap-2">
          <input value={testDest} onChange={(e) => setTestDest(e.target.value)}
            placeholder={channel === "email" ? "email@test.it" : "+39 333 0000000"}
            className={inputCls} data-testid="test-destination" />
          <button type="button" onClick={sendTest} data-testid="test-send-btn"
            className="h-10 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 shrink-0">
            <PaperPlaneTilt size={14} weight="bold" /> Test
          </button>
        </div>
        {testResult && (
          <div className="text-xs text-zinc-600 bg-zinc-50 rounded-md p-2" data-testid="test-result">
            {testResult.sent && !testResult.mock
              ? <>Inviato <b>davvero</b> a {testResult.destination}</>
              : <>Anteprima per {testResult.destination} (nessun invio reale)</>}
            {testResult.rendered?.body && <div className="mt-1 italic">"{testResult.rendered.body.slice(0, 120)}"</div>}
          </div>
        )}
      </div>
    </div>
  );
}
